import api from "@/lib/api";
import { formatRupiah, formatAkuntansi } from "@/lib/format";

const COMPANY = {
  name: "PT AMAYDELLI BERKAH SEJAHTERA",
  tagline: "Building Brands, Creating Growth.",
  address: "Jl. Teladan No. 19B, Gegerkalong, Kecamatan Sukasari, Kota Bandung, Jawa Barat 40153",
  contact: "Telp/WA: 0811-2128-485 &nbsp;|&nbsp; Email: amaydelli189@gmail.com",
  npwp: "NPWP: 62.524.603.8-428.000",
};

const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const num = (v) => (v === null || v === undefined || v === "" ? "-" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: 2 }));
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const dt = (v) => {
  const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${Number(m[3])} ${BULAN[Number(m[2]) - 1]} ${m[1]}` : "-";
};

async function findOne(entity, code) {
  if (!code) return null;
  const { data } = await api.get(`/masters/${entity}`, { params: { q: code, status: "all" } });
  return (data || []).find((x) => x.code === code) || null;
}

// UNIT/BOX: nilai manual per baris (box_qty) > konversi produk qty_per_carton > "-"
export function boxQty(line, product) {
  if (line.box_qty !== null && line.box_qty !== undefined && line.box_qty !== "") return Number(line.box_qty);
  const per = Number(product?.qty_per_carton || 0);
  return per > 0 ? Number(line.qty || 0) / per : null;
}

const CSS = `
@page{size:A4;margin:1.47cm 1.65cm 1.4cm 1.65cm}
*{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;color:#172B48;font-size:10pt;margin:0}
.hdr{display:flex;gap:14px;align-items:center}.hdr img{width:2.58cm;height:auto;flex:none}
.co-name{font-weight:400;font-size:14pt}.co-tag{color:#B58A32;font-weight:700;font-size:10pt}.co-sub{color:#657487;font-size:9pt;line-height:1.35}
.rule{border-bottom:1.5px solid #172B48;margin:8px 0 12px}
.title{font-weight:400;font-size:20pt;letter-spacing:1px;margin:0 0 10px}
.info{display:grid;grid-template-columns:60% 39%;gap:1%;margin-bottom:12px}.box{background:#F2F5F9;padding:8px 10px;color:#657487;font-size:9pt;line-height:1.5}
.box b.lbl{display:block;color:#657487;font-size:9pt;margin-bottom:2px}.box .main{color:#172B48;font-weight:700}
table.items{width:100%;border-collapse:collapse;font-size:9pt}table.items th{background:#172B48;color:#fff;font-weight:700;text-align:center;padding:6px 5px;border:1px solid #172B48}
table.items td{border:1px solid #CBD3DD;padding:5px;vertical-align:top}table.items tbody tr:nth-child(odd) td{background:#F5F7FA}
table.items tr{page-break-inside:avoid;break-inside:avoid}thead{display:table-header-group}tfoot{display:table-row-group}
table.items tfoot td{background:#F2F5F9;font-weight:700}.c{text-align:center}.r{text-align:right;white-space:nowrap}
.tail{page-break-inside:avoid;break-inside:avoid;margin-top:12px}
.sum{width:50%;margin-left:auto;border-top:1.5px solid #172B48;border-collapse:collapse}.sum td{padding:4px 6px;text-align:right}.sum tr.tot td{font-weight:700;font-size:11pt}
.terms{margin-top:14px}.terms h4{margin:0 0 4px;font-size:10pt}.terms ul{margin:0;padding-left:16px;color:#657487}
.sign{display:grid;grid-template-columns:1fr 1fr;border-top:1.5px solid #172B48;margin-top:18px;padding-top:6px;text-align:center}
.sign b{display:block;margin-bottom:46px}.sign .who{color:#657487;font-size:9pt;line-height:1.5}
@media screen{body{background:#e5e7eb}.page{background:#fff;width:21cm;min-height:29.7cm;margin:16px auto;padding:1.47cm 1.65cm 1.4cm;box-shadow:0 2px 12px rgba(0,0,0,.15)}}
@media print{.page{padding:0}}
`;

function summaryRows(t, isInvoice) {
  const s = t.totals || {};
  const other = Number(s.dpp || 0) - Number(s.subtotal || 0);
  const rows = [["Subtotal", s.subtotal], ["Lain-lain", other]];
  if (isInvoice) {
    rows.push(["TOTAL SEBELUM PAJAK", s.dpp, true]);
    if (Number(s.tax || 0)) rows.push(["Pajak", s.tax], ["TOTAL TAGIHAN", s.amount, true]);
  } else {
    if (Number(s.tax || 0)) rows.push(["Pajak", s.tax]);
    rows.push(["TOTAL", s.amount, true]);
  }
  return rows.map(([l, v, b]) => `<tr class="${b ? "tot" : ""}"><td>${l}</td><td style="width:45%">${formatAkuntansi(v || 0)}</td></tr>`).join("");
}

export function buildDocHtml({ t, kind, party, warehouse, products }) {
  const isInvoice = kind === "sales_invoice";
  const pmap = Object.fromEntries((products || []).map((p) => [p.sku, p]));
  let tq = 0, tb = 0, hasBox = false;
  const body = (t.lines || []).map((l, i) => {
    const p = pmap[l.product_sku];
    const b = boxQty(l, p);
    tq += Number(l.qty || 0);
    if (b !== null) { tb += b; hasBox = true; }
    const name = [p?.name, p?.variant].filter(Boolean).join(" ") || l.description || l.product_sku || "";
    return `<tr><td class="c">${i + 1}</td><td>${esc(name)}</td><td class="r">${num(l.qty)}</td><td class="r">${b === null ? "-" : num(b)}</td><td class="r">${formatRupiah(l.price)}</td><td class="r">${formatRupiah(l.gross ?? l.qty * l.price)}</td></tr>`;
  }).join("");
  const contact = [party?.phone, party?.email].filter(Boolean).join(" / ") || "-";
  const addr = [party?.address, party?.city, party?.province].filter(Boolean).join(", ") || "-";
  const terms = isInvoice
    ? ["Pembayaran melalui Bank BCA, a.n. PT AMAYDELLI BERKAH SEJAHTERA.", "Nomor rekening: 233-107-9385.", "Cantumkan nomor invoice pada berita transfer; pajak mengikuti ketentuan berlaku."]
    : [`Alamat pengiriman: ${esc([warehouse?.name, warehouse?.address].filter(Boolean).join(" — ") || "-")}.`, `Termin pembayaran: ${esc(t.payment_term || party?.payment_term || "-")}.`, "Barang yang diterima wajib sesuai spesifikasi dan jumlah PO."];
  if (t.notes) terms.push(esc(t.notes));
  const approved = ["DISETUJUI", "SEBAGIAN", "SELESAI", "DIBAYAR", "LUNAS"].includes(t.status) || t.approved_at;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(t.number)}</title><style>${CSS}@page{@bottom-center{content:"${COMPANY.name} - ${isInvoice ? "INVOICE" : "PURCHASE ORDER"}";font-size:7pt;color:#657487;font-family:Arial}}</style></head><body><div class="page" data-testid="print-doc">
  <div class="hdr"><img src="${window.location.origin}/abs-logo-doc.png" alt="Logo ABS"/><div><div class="co-name">${COMPANY.name}</div><div class="co-tag">${COMPANY.tagline}</div><div class="co-sub">${COMPANY.address}<br/>${COMPANY.contact}<br/>${COMPANY.npwp}</div></div></div>
  <div class="rule"></div><div class="title">${isInvoice ? "INVOICE" : "PURCHASE ORDER"}</div>
  <div class="info"><div class="box"><b class="lbl">${isInvoice ? "DITAGIHKAN KEPADA" : "PEMASOK / VENDOR"}</b><div class="main">${esc(party?.company_name || party?.name || t.party_name || "-")}</div>Alamat: ${esc(addr)}<br/>Kontak: ${esc(contact)}</div>
  <div class="box"><b class="lbl">INFORMASI DOKUMEN</b>Nomor: <span class="main">${esc(t.number)}</span><br/>Tanggal: ${dt(t.date)}<br/>${isInvoice ? `Jatuh Tempo: ${dt(t.due_date)}` : `Pengiriman: ${dt(t.delivery_date)}`}<br/>Referensi: ${esc(t.reference_number || t.so_number || "-")}</div></div>
  <table class="items"><thead><tr><th style="width:5%">NO</th><th>DESKRIPSI PRODUK</th><th style="width:11%">QTY (PCs)</th><th style="width:11%">UNIT/BOX</th><th style="width:14%">UNIT PRICE</th><th style="width:20%">TOTAL</th></tr></thead>
  <tbody>${body}</tbody><tfoot><tr><td></td><td class="c">Total</td><td class="r">${num(tq)}</td><td class="r">${hasBox ? num(tb) : "-"}</td><td></td><td></td></tr></tfoot></table>
  <div class="tail"><table class="sum">${summaryRows(t, isInvoice)}</table>
  <div class="terms"><h4>KETENTUAN &amp; CATATAN</h4><ul>${terms.map((x) => `<li>${x}</li>`).join("")}</ul></div>
  <div class="sign"><div><b>DIBUAT OLEH</b><div class="who">Nama: ${esc(t.created_by || "-")}<br/>Jabatan: ____________<br/>Tanggal: ${dt(t.created_at)}</div></div>
  <div><b>DISETUJUI OLEH</b><div class="who">Nama: ${esc(approved ? t.approved_by || "-" : "____________")}<br/>Jabatan: ____________<br/>Tanggal: ${approved ? dt(t.approved_at) : "____________"}</div></div></div></div>
  </div></body></html>`;
}

export async function openTxnDocument(t, kind, autoPrint) {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return;
  w.document.write("<p style='font-family:Arial;padding:24px'>Menyiapkan dokumen...</p>");
  const isInvoice = kind === "sales_invoice";
  const [party, warehouse, prod] = await Promise.all([
    findOne(isInvoice ? "customers" : "suppliers", t.party_code).catch(() => null),
    isInvoice ? null : findOne("warehouses", t.warehouse_code).catch(() => null),
    api.get("/masters/products", { params: { status: "all" } }).then((r) => r.data).catch(() => []),
  ]);
  const skus = new Set((t.lines || []).map((l) => l.product_sku));
  const html = buildDocHtml({ t, kind, party, warehouse, products: prod.filter((p) => skus.has(p.sku)) });
  w.document.open();
  w.document.write(autoPrint ? html.replace("</body>", "<script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body>") : html);
  w.document.close();
}
